import { DOCUMENT } from "@angular/common";
import { inject, Injectable, InjectionToken, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { PluginInjectorWidget, SessionUserService } from "core-client/api";
import { BehaviorSubject, Observable, Subscription } from "rxjs";

import { WidgetScreen } from "./widget-screen";
import {
  WIDGET_WINDOW_CHANNEL_NAME,
  WidgetWindowChannel,
  WidgetWindowMessage,
  widgetWindowName,
} from "./widget-window.messages";

export const WIDGET_WINDOW_CHANNEL_FACTORY = new InjectionToken<() => WidgetWindowChannel>(
  "WIDGET_WINDOW_CHANNEL_FACTORY",
  { providedIn: "root", factory: () => () => new BroadcastChannel(WIDGET_WINDOW_CHANNEL_NAME) },
);

const DEFAULT_WINDOW_FEATURES = "popup=yes,width=1024,height=640";
const REPORT_TIMEOUT_MS = 15_000;
const REPORT_POLL_MS = 1_000;

function windowFeatures(screen?: WidgetScreen): string {
  return screen ? `popup=yes,width=${screen.width},height=${screen.height}` : DEFAULT_WINDOW_FEATURES;
}

@Injectable({ providedIn: "root" })
export class WidgetWindowService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly createChannel = inject(WIDGET_WINDOW_CHANNEL_FACTORY);
  private readonly sessionUser = inject(SessionUserService);
  private readonly windows = new Map<string, Window>();
  private readonly reportWatches = new Map<string, ReturnType<typeof setInterval>>();
  private readonly externalIds = new BehaviorSubject<ReadonlySet<string>>(new Set());
  private channel: WidgetWindowChannel | undefined;
  private logoutSubscription: Subscription | undefined;
  private readonly closeAll = (): void => {
    for (const widgetId of new Set([...this.externalIds.value, ...this.windows.keys()])) {
      this.post({ type: "close", widgetId });
    }
    for (const opened of this.windows.values()) {
      if (!opened.closed) {
        opened.close();
      }
    }
  };

  readonly externalWidgetIds$: Observable<ReadonlySet<string>> = this.externalIds.asObservable();

  get supported(): boolean {
    return typeof BroadcastChannel !== "undefined";
  }

  start(): void {
    if (this.channel || !this.supported) {
      return;
    }
    this.channel = this.createChannel();
    this.channel.addEventListener("message", (event: MessageEvent<WidgetWindowMessage>) =>
      this.handle(event.data),
    );
    this.document.defaultView?.addEventListener("pagehide", this.closeAll);
    this.logoutSubscription = this.sessionUser.loggedOut$.subscribe(this.closeAll);
    this.post({ type: "who-is-there" });
  }

  isExternal(widgetId: string): boolean {
    return this.externalIds.value.has(widgetId);
  }

  open(widget: PluginInjectorWidget.IWidget, targetScreen?: WidgetScreen): boolean {
    const existing = this.windows.get(widget._id);
    if (existing && !existing.closed) {
      existing.focus();
      return true;
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree(["/dashboard", "widget", widget._id]),
    );
    const opened = this.document.defaultView?.open(
      url,
      widgetWindowName(widget._id),
      windowFeatures(targetScreen),
    );
    if (!opened) {
      console.warn(`[WidgetWindow] browser blocked the window for widget ${widget._id}`);
      return false;
    }
    this.windows.set(widget._id, opened);
    if (targetScreen) {
      opened.moveTo(targetScreen.left, targetScreen.top);
    }
    this.setExternal(widget._id, true);
    this.watchForReport(widget._id, opened);
    console.debug(`[WidgetWindow] opened widget ${widget._id} in its own window, waiting for it to report`);
    return true;
  }

  focus(widgetId: string): void {
    const existing = this.windows.get(widgetId);
    if (existing?.closed) {
      this.forget(widgetId);
      return;
    }
    if (existing) {
      existing.focus();
    } else {
      this.post({ type: "focus", widgetId });
    }
  }

  bringBack(widgetId: string): void {
    this.post({ type: "close", widgetId });
    this.windows.get(widgetId)?.close();
    this.forget(widgetId);
  }

  ngOnDestroy(): void {
    for (const widgetId of [...this.reportWatches.keys()]) {
      this.stopReportWatch(widgetId);
    }
    this.document.defaultView?.removeEventListener("pagehide", this.closeAll);
    this.logoutSubscription?.unsubscribe();
    this.channel?.close();
    this.channel = undefined;
  }

  private handle(message: WidgetWindowMessage): void {
    if (message.type === "hello") {
      this.stopReportWatch(message.widgetId);
      this.setExternal(message.widgetId, true);
    } else if (message.type === "bye") {
      this.forget(message.widgetId);
    }
  }

  private forget(widgetId: string): void {
    this.stopReportWatch(widgetId);
    this.windows.delete(widgetId);
    this.setExternal(widgetId, false);
  }

  private watchForReport(widgetId: string, opened: Window): void {
    this.stopReportWatch(widgetId);
    let waited = 0;
    const watch = setInterval(() => {
      waited += REPORT_POLL_MS;
      if (opened.closed) {
        console.warn(`[WidgetWindow] window for widget ${widgetId} closed before it reported, widget restored in the dashboard`);
        this.forget(widgetId);
      } else if (waited >= REPORT_TIMEOUT_MS) {
        console.warn(`[WidgetWindow] no report from the window for widget ${widgetId} within ${REPORT_TIMEOUT_MS / 1000} s, widget restored in the dashboard`);
        this.forget(widgetId);
      }
    }, REPORT_POLL_MS);
    this.reportWatches.set(widgetId, watch);
  }

  private stopReportWatch(widgetId: string): void {
    const watch = this.reportWatches.get(widgetId);
    if (watch !== undefined) {
      clearInterval(watch);
      this.reportWatches.delete(widgetId);
    }
  }

  private setExternal(widgetId: string, external: boolean): void {
    if (this.externalIds.value.has(widgetId) === external) {
      return;
    }
    const next = new Set(this.externalIds.value);
    if (external) {
      next.add(widgetId);
    } else {
      next.delete(widgetId);
      console.debug(`[WidgetWindow] widget ${widgetId} is back in the dashboard`);
    }
    this.externalIds.next(next);
  }

  private post(message: WidgetWindowMessage): void {
    this.channel?.postMessage(message);
  }
}
